import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Pagination,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Add,
  Search,
  Edit,
  Delete,
  MoreVert,
  AccessTime,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { shiftManagementAPI } from "../../services/api";
import ShiftFormDialog from "./ShiftFormDialog";
import "./ShiftManagement.css";

const ShiftManagement = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);

  const loadShifts = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: 10,
        search: searchTerm,
      };

      const response = await shiftManagementAPI.getAll(params);

      if (response.data.success) {
        setShifts(response.data.data.shifts || []);
        setTotalPages(response.data.data.totalPages || 1);
        setTotal(response.data.data.total || 0);
      }
    } catch (error) {
      console.error("Error loading shifts:", error);
      enqueueSnackbar("Failed to load shifts", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm, enqueueSnackbar]);

  useEffect(() => {
    loadShifts();
  }, [loadShifts]);

  const handleAddShift = () => {
    setSelectedShift(null);
    setOpenDialog(true);
  };

  const handleEditShift = async (shift) => {
    try {
      setLoading(true);
      // Fetch full shift details by ID
      const response = await shiftManagementAPI.getById(shift._id);
      if (response.data.success) {
        setSelectedShift(response.data.data.shift || response.data.data);
        setOpenDialog(true);
      } else {
        // Fallback to the shift object from list if API fails
        setSelectedShift(shift);
        setOpenDialog(true);
      }
    } catch (error) {
      console.error("Error fetching shift details:", error);
      // Fallback to the shift object from list
      setSelectedShift(shift);
      setOpenDialog(true);
      enqueueSnackbar("Could not load full shift details, showing partial data", {
        variant: "warning",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteShift = async (shiftId) => {
    if (window.confirm("Are you sure you want to delete this shift?")) {
      try {
        await shiftManagementAPI.delete(shiftId);
        enqueueSnackbar("Shift deleted successfully", { variant: "success" });
        loadShifts();
      } catch (error) {
        enqueueSnackbar(
          error.response?.data?.message || "Failed to delete shift",
          { variant: "error" }
        );
      }
    }
  };

  const handleToggleStatus = async (shiftId) => {
    try {
      await shiftManagementAPI.toggleStatus(shiftId);
      enqueueSnackbar("Shift status updated successfully", {
        variant: "success",
      });
      loadShifts();
    } catch (error) {
      enqueueSnackbar("Failed to update shift status", { variant: "error" });
    }
  };

  const handleDialogClose = (shouldRefresh) => {
    setOpenDialog(false);
    setSelectedShift(null);
    if (shouldRefresh) {
      loadShifts();
    }
  };

  const handleMenuOpen = (event, shift) => {
    setAnchorEl(event.currentTarget);
    setSelectedShift(shift);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedShift(null);
  };

  const formatDuration = (minutes) => {
    if (!minutes || minutes < 0) return "N/A";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const calculateDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return 0;
    const start = new Date(`2000-01-01T${startTime}`);
    const end = new Date(`2000-01-01T${endTime}`);
    let diff = (end - start) / 60000; // minutes
    if (diff < 0) diff += 1440; // Handle overnight shifts
    return diff;
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  return (
    <Box className="shift-management-container">
      {/* Header Section */}
      <Box className="page-header">
        <Box>
          <Typography variant="h4" className="page-title">
            Shift Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage work shifts and schedules
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddShift}
          className="add-button"
        >
          Add New Shift
        </Button>
      </Box>

      {/* Main Content Card */}
      <Card className="content-card">
        <CardContent>
          {/* Search Bar */}
          <Box className="search-container">
            <TextField
              placeholder="Search shifts by name or code..."
              variant="outlined"
              size="small"
              fullWidth
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              className="search-field"
            />
          </Box>

          {/* Table */}
          {loading ? (
            <Box className="loading-container">
              <CircularProgress />
            </Box>
          ) : shifts.length === 0 ? (
            <Box className="no-data-container">
              <AccessTime className="no-data-icon" />
              <Typography variant="h6" color="text.secondary">
                No shifts found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your search or create a new shift
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Shift Name</TableCell>
                      <TableCell>Code</TableCell>
                      <TableCell>Timing</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>Schedule Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {shifts.map((shift) => (
                      <TableRow key={shift._id} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body1" fontWeight={600}>
                              {shift.name || shift.shiftName}
                            </Typography>
                            {shift.code && (
                              <Typography variant="caption" color="text.secondary">
                                Code: {shift.code || shift.shiftCode}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={shift.code || shift.shiftCode || "N/A"} 
                            size="small" 
                            variant="outlined" 
                          />
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {shift.startTime || shift.shiftBeginTime} - {shift.endTime || shift.shiftEndTime}
                            </Typography>
                            {shift.startTime && shift.endTime && (
                              <Typography variant="caption" color="text.secondary">
                                Duration: {formatDuration(calculateDuration(shift.startTime || shift.shiftBeginTime, shift.endTime || shift.shiftEndTime))}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {shift.startTime && shift.endTime
                              ? formatDuration(calculateDuration(shift.startTime || shift.shiftBeginTime, shift.endTime || shift.shiftEndTime))
                              : shift.shiftDurationMinutes 
                              ? formatDuration(shift.shiftDurationMinutes)
                              : "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={(shift.scheduleType || shift.shiftScheduleType || "REGULAR").toUpperCase()}
                            size="small"
                            color={
                              (shift.scheduleType || shift.shiftScheduleType || "regular").toLowerCase() === "regular"
                                ? "success"
                                : (shift.scheduleType || shift.shiftScheduleType || "regular").toLowerCase() === "flexible"
                                ? "primary"
                                : "warning"
                            }
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={shift.isActive !== undefined ? (shift.isActive ? "Active" : "Inactive") : (shift.status ? "Active" : "Inactive")}
                            size="small"
                            color={(shift.isActive !== undefined ? shift.isActive : shift.status) ? "success" : "default"}
                            onClick={() => handleToggleStatus(shift._id)}
                            className="status-chip"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, shift)}
                          >
                            <MoreVert />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <Box className="pagination-container">
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(e, value) => setPage(value)}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", textAlign: "center", mt: 1 }}
              >
                Total: {total} shifts
              </Typography>
            </>
          )}
        </CardContent>
      </Card>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            handleEditShift(selectedShift);
            handleMenuClose();
          }}
        >
          <Edit sx={{ mr: 1 }} fontSize="small" />
          Edit Shift
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleDeleteShift(selectedShift._id);
            handleMenuClose();
          }}
          sx={{ color: "error.main" }}
        >
          <Delete sx={{ mr: 1 }} fontSize="small" />
          Delete Shift
        </MenuItem>
      </Menu>

      {/* Shift Form Dialog */}
      <ShiftFormDialog
        open={openDialog}
        shift={selectedShift}
        onClose={handleDialogClose}
      />
    </Box>
  );
};

export default ShiftManagement;
